/*
 * Collection（商品一覧/PLP）固有スクリプト
 * 2026-08-20：ネーム印向けダミーデータ（series/kisei/size/shape、絞り込み・比較機能込み）を
 * 撤去し、実データ（sections/main-collection.liquidが埋め込むdata-plp-items）で描画する
 * 方式に変更（ユーザー承認：「商品グリッドだけ実データ化」。絞り込み・比較は実タグに
 * 基づく汎用設計が別途必要なため今回のスコープ外）。並び替え・ページングのみ担当。
 * カート追加は実データに合わせてAjax Cart API（cart-api.js）に接続する
 * （旧実装はSANBY.utilsの旧localStorageモックcartを参照しており、既に廃止済みで動作していなかった）。
 */
(function (SANBY) {
  const { escapeHtml } = SANBY.utils;
  const { addItem } = SANBY.cartApi;

  function readItems() {
    const el = document.querySelector("[data-plp-items]");
    if (!el) return [];
    try {
      return JSON.parse(el.textContent || "[]");
    } catch (e) {
      console.error("商品データの読み込みに失敗しました:", e);
      return [];
    }
  }

  const productsData = readItems();

  // Shopify の {% paginate %} タグは1ページ最大250件までしか指定できない
  // （Liquid側もこの上限で取得しているため、JS側のページサイズも合わせる）。
  const PAGE_SIZE = 250;

  const state = {
    cartAdded: {},
    cartLoading: {},
    sortBy: "recommend",
    currentPage: 1,
  };
  const cartTimers = {};

  // ---------- カート追加（実データ連携） ----------
  function handleAddToCart(handle) {
    if (state.cartLoading[handle]) return; // 通信中の連打防止
    const p = productsData.find((x) => x.handle === handle);
    if (!p || !p.variantId) return;

    state.cartLoading[handle] = true;
    render();

    addItem({ id: p.variantId, quantity: 1 })
      .then(() => {
        state.cartLoading[handle] = false;
        state.cartAdded[handle] = true;
        render();
        clearTimeout(cartTimers[handle]);
        cartTimers[handle] = setTimeout(() => {
          state.cartAdded[handle] = false;
          render();
        }, 1800);
      })
      .catch((err) => {
        console.error("カートへの追加に失敗しました:", err);
        state.cartLoading[handle] = false;
        render();
      });
  }

  // ---------- 並び替え・ページング ----------
  function getSorted() {
    let list = productsData;
    if (state.sortBy === "priceAsc") list = [...list].sort((a, b) => a.price - b.price);
    else if (state.sortBy === "priceDesc") list = [...list].sort((a, b) => b.price - a.price);
    else if (state.sortBy === "new") list = [...list].sort((a, b) => b.createdAt - a.createdAt);
    return list; // 'recommend' はコレクションの並び順（Liquid側の掲載順）のまま
  }

  function paginate(list) {
    const totalPages = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
    if (state.currentPage > totalPages) state.currentPage = totalPages;
    const start = (state.currentPage - 1) * PAGE_SIZE;
    return { pageItems: list.slice(start, start + PAGE_SIZE), totalPages };
  }

  // ---------- 描画 ----------
  function productCardHtml(p) {
    const added = !!state.cartAdded[p.handle];
    const loading = !!state.cartLoading[p.handle];
    const priceFmt = Number(p.price).toLocaleString("ja-JP");
    let btnClass = "plp-cart-btn";
    let btnLabel = "カートに入れる";
    if (loading) {
      btnClass += " is-loading";
      btnLabel = `<span class="plp-cart-spinner" aria-hidden="true"></span><span class="visually-hidden">追加中...</span>`;
    } else if (added) {
      btnClass += " is-added";
      btnLabel = "追加しました ✓";
    }
    return `
      <div class="plp-card">
        <a href="${escapeHtml(p.url)}" class="plp-card-link">
          <div class="plp-card-img-wrap">
            <div class="plp-card-img">${p.image ? `<img src="${escapeHtml(p.image)}" alt="${escapeHtml(p.title)}" loading="lazy">` : "商品画像"}</div>
            ${p.available ? "" : `<span class="plp-card-badge">SOLD OUT</span>`}
          </div>
          <div class="plp-card-name">${escapeHtml(p.title)}</div>
          <div class="plp-card-price">¥${priceFmt}</div>
        </a>
        ${
          p.available && p.variantId && !p.needsOptions
            ? `<button type="button" class="${btnClass}" data-add-cart="${escapeHtml(p.handle)}"${loading ? " disabled" : ""}>${btnLabel}</button>`
            : `<a href="${escapeHtml(p.url)}" class="plp-product-link">商品ページへ</a>`
        }
      </div>`;
  }

  function render() {
    const sorted = getSorted();
    const { pageItems, totalPages } = paginate(sorted);
    const totalCount = sorted.length;
    const start = totalCount === 0 ? 0 : (state.currentPage - 1) * PAGE_SIZE + 1;
    const end = start === 0 ? 0 : start + pageItems.length - 1;

    const countEl = document.querySelector("[data-result-count]");
    countEl.textContent = totalCount === 0 ? "0件" : `${totalCount}件中 ${start}-${end}件を表示`;

    const grid = document.querySelector("[data-product-grid]");
    const empty = document.querySelector("[data-empty-state]");
    const pag = document.querySelector("[data-pagination]");

    if (totalCount === 0) {
      grid.hidden = true;
      grid.innerHTML = "";
      empty.hidden = false;
      pag.hidden = true;
      pag.innerHTML = "";
      return;
    }

    grid.hidden = false;
    grid.innerHTML = pageItems.map(productCardHtml).join("");
    empty.hidden = true;

    if (totalPages <= 1) {
      pag.hidden = true;
      pag.innerHTML = "";
    } else {
      pag.hidden = false;
      let html = "";
      for (let i = 1; i <= totalPages; i++) {
        html += `<button type="button" class="plp-page-btn${i === state.currentPage ? " is-current" : ""}" data-page="${i}" ${i === state.currentPage ? "disabled" : ""}>${i}</button>`;
      }
      html += `<button type="button" class="plp-page-next" data-page-next ${state.currentPage >= totalPages ? "disabled" : ""}>次へ ›</button>`;
      pag.innerHTML = html;
    }
  }

  // ---------- イベント ----------
  function initEvents() {
    document.addEventListener("change", (e) => {
      if (e.target.matches("[data-sort-select]")) {
        state.sortBy = e.target.value;
        state.currentPage = 1;
        render();
      }
    });

    document.addEventListener("click", (e) => {
      const addBtn = e.target.closest("[data-add-cart]");
      if (addBtn) {
        handleAddToCart(addBtn.dataset.addCart);
        return;
      }
      const pageBtn = e.target.closest(".plp-page-btn[data-page]");
      if (pageBtn && !pageBtn.disabled) {
        state.currentPage = Number(pageBtn.dataset.page);
        render();
        document.querySelector(".plp-toolbar")?.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      const nextBtn = e.target.closest("[data-page-next]");
      if (nextBtn && !nextBtn.disabled) {
        state.currentPage += 1;
        render();
        document.querySelector(".plp-toolbar")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
  }

  document.addEventListener("DOMContentLoaded", () => {
    initEvents();
    render();
  });
})(window.SANBY);
